/**
 * Created by rathompson on 10/19/23
 * File: ProductInfoModal.tsx
 * Project: cloud-mgmt-modules ©
 * ©
 */


import type { VFC } from 'react'
import type { ModalProps } from '@panwds/react-ui'
import { Body, Bold, Card, CopyButton, Modal, ModalBody, ModalCloseButton, ModalFooter, ModalHeader, noop, useModal } from '@panwds/react-ui'
import { useCallback, useContext, useMemo } from 'react'
import type { Instance } from '@sparky/framework'
import { CopyIcon } from '@panwds/icons'
import { BUTTON_CLOSE, TITLE_SUPPORT_INFO } from '../../../../common/constants/LabelConstants'
import GlobalContext from '../../../../common/context/GlobalContext'
import AppContext from '../../../../common/context/AppContext'
import StringTemplate from '../../../../common/utils/StringTemplate'
import { ERROR_RESOLUTION_SUBTEXT, LINK_SUPPORT_CASE } from '../../../constants/LabelConstants'
import { isBundledProduct } from '../../../../activation/components/common/utils'

const ProductInfoFields = {
  TSG_ID: 'TSG ID',
  TSG_NAME: 'Tenant Name',
  ID: 'Instance ID',
  REGION: 'Data Region',
  STATUS: 'Instance Status',
  PROV_STATUS: 'Provisioning Status'
}

const SupportInfoFields = {
  CSP_ID: 'CSP Account ID',
  CSP_NAME: 'CSP Account Name',
  CSP_TENANT_ID: 'CSP Tenant ID',
  SERIAL_NUM: 'Serial Number'
}

const InfoCard: VFC<{
  title: string,
  fields?: Array<[string, string]>,
  info: Record<string, any>,
  children?: any
}> = ({
  title = '',
  fields = [],
  info = {},
  children
}) => {

  const copyText = [...fields, ['DETAILS', JSON.stringify(info, null, 2)]].map(([name, value]) => `${name}: ${value}`).join('\n')

  const table = useMemo(() => {
    return (
      <table className="cm-w-full cm-mt-2 cm-mb-4" style={{ tableLayout: 'fixed' }}>
        <tbody>
          {fields.map(([name, value]) => (
            <tr key={name}>
              <td className="cm-align-top cm-truncate"><Bold appearance='semibold'>{name}</Bold></td>
              <td className="cm-align-top"><Body truncate>{value}</Body></td>
            </tr>
          ))}
        </tbody>
      </table>
    )
  }, [fields])

  return (
    <Card addClassName='cm-flex-1 cm-p-2'>
      <div className='cm-flex cm-flex-row cm-justify-between'>
        <Bold appearance='semibold'>{title}</Bold>
        <CopyButton icon={<CopyIcon size='sm'/>} text={copyText}/>
      </div>
      {table}
      {children}
    </Card>
  )
}

const ProductInfoModal: VFC<ModalProps & {
  closeModal: any,
  instance: Instance
}> = ({ isOpen, closeModal, instance }) => {

  const { getAppName, apps } = useContext(AppContext)
  const { treeReducer } = useContext(GlobalContext)
  const { sgKeyBy } = treeReducer

  const {
    tsg_id,
    tenant_id,
    csp_tenant_id,
    contract,
    support_account_id,
    support_account_name,
    license_id,
    app_id,
    platform_region,
    region,
    status,
    provisioning_status
  } = instance
  const serial_numbers = isBundledProduct(apps, app_id) ? instance?.entitlements?.map((e: any) => e.serial_number)?.join(', ') : instance?.serial_number
  const info = { tsg_id, tenant_id, csp_tenant_id, support_account_id, license_id, serial_numbers }

  const tsg = sgKeyBy[tsg_id as string]

  const onClose = useCallback(() => {
    closeModal()
  }, [closeModal])

  const productInformation = useMemo(() => {
    return [
      [ProductInfoFields.TSG_ID, tsg_id],
      [ProductInfoFields.TSG_NAME, tsg?.display_name],
      [ProductInfoFields.ID, tenant_id],
      [ProductInfoFields.REGION, platform_region || region],
      [ProductInfoFields.STATUS, status],
      [ProductInfoFields.PROV_STATUS, provisioning_status],
    ].filter(field => field[1])
  }, [platform_region, provisioning_status, region, status, tenant_id, tsg?.display_name, tsg_id])

  const supportInformation = useMemo(() => {
    return [
      [SupportInfoFields.CSP_ID, support_account_id],
      [SupportInfoFields.CSP_NAME, support_account_name],
      [SupportInfoFields.CSP_TENANT_ID, csp_tenant_id],
      [SupportInfoFields.SERIAL_NUM, (contract || serial_numbers)]
    ].filter(field => field[1])
  }, [contract, csp_tenant_id, serial_numbers, support_account_id, support_account_name])

  return (
    <Modal {...{ isOpen, onClose }} size='lg'>
      <ModalHeader title='Information'/>
      <ModalBody addClassName='cm-flex cm-flex-col'>
        <div className='cm-flex-1 cm-flex cm-flex-row cm-justify-between cm-gap-2'>
          <InfoCard title={getAppName(app_id)} fields={productInformation as any} info={info}/>
          <InfoCard title={TITLE_SUPPORT_INFO} fields={supportInformation as any} info={info}>
            <Body addClassName="cm-mt-2 cm-mb-2" size="lg">{StringTemplate(ERROR_RESOLUTION_SUBTEXT, (
              <><br/>
                <a
                  href="https://supportcases.paloaltonetworks.com/"
                  target="_blank"
                  rel="noopener noreferrer">{LINK_SUPPORT_CASE}</a>
              </>
            ))}</Body>
          </InfoCard>
        </div>
      </ModalBody>
      <ModalFooter>
        <ModalCloseButton appearance='secondary'>{BUTTON_CLOSE}</ModalCloseButton>
      </ModalFooter>
    </Modal>
  )
}

export const useProductInfoModule = (instance: Instance) => {
  const { openModal, closeModal } = useModal(ProductInfoModal)
  return useMemo(() => ({
    openModal: instance ? openModal.bind(undefined, { instance } as any) : noop,
    closeModal,
  }), [openModal, instance, closeModal])
}

export default ProductInfoModal
